'use client';

import { useEffect, useRef } from 'react';

interface Props {
  /** Plain text — auto-splits to two rows if > 5 chars and no spaces. Pass a space to force split. */
  text: string;
  /** Approximate target visual width (CSS px). Pitch is derived to fit. */
  width?: number;
  /** Convergence duration (ms). */
  duration?: number;
}

// 5×7 bitmap font — lifted verbatim from public/secret/dot-matrix-loader/
const F = (...rows: number[]) => rows;
const FONT: Record<string, number[]> = {
  ' ': F(0, 0, 0, 0, 0, 0, 0),
  '.': F(0, 0, 0, 0, 0, 0, 0b00100),
  '-': F(0, 0, 0, 0b01110, 0, 0, 0),
  '_': F(0, 0, 0, 0, 0, 0, 0b11111),
  A: F(0b01110, 0b10001, 0b10001, 0b11111, 0b10001, 0b10001, 0b10001),
  B: F(0b11110, 0b10001, 0b10001, 0b11110, 0b10001, 0b10001, 0b11110),
  C: F(0b01110, 0b10001, 0b10000, 0b10000, 0b10000, 0b10001, 0b01110),
  D: F(0b11110, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b11110),
  E: F(0b11111, 0b10000, 0b10000, 0b11110, 0b10000, 0b10000, 0b11111),
  F: F(0b11111, 0b10000, 0b10000, 0b11110, 0b10000, 0b10000, 0b10000),
  G: F(0b01110, 0b10001, 0b10000, 0b10111, 0b10001, 0b10001, 0b01110),
  H: F(0b10001, 0b10001, 0b10001, 0b11111, 0b10001, 0b10001, 0b10001),
  I: F(0b01110, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b01110),
  J: F(0b00111, 0b00010, 0b00010, 0b00010, 0b00010, 0b10010, 0b01100),
  K: F(0b10001, 0b10010, 0b10100, 0b11000, 0b10100, 0b10010, 0b10001),
  L: F(0b10000, 0b10000, 0b10000, 0b10000, 0b10000, 0b10000, 0b11111),
  M: F(0b10001, 0b11011, 0b10101, 0b10101, 0b10001, 0b10001, 0b10001),
  N: F(0b10001, 0b10001, 0b11001, 0b10101, 0b10011, 0b10001, 0b10001),
  O: F(0b01110, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01110),
  P: F(0b11110, 0b10001, 0b10001, 0b11110, 0b10000, 0b10000, 0b10000),
  Q: F(0b01110, 0b10001, 0b10001, 0b10001, 0b10101, 0b10010, 0b01101),
  R: F(0b11110, 0b10001, 0b10001, 0b11110, 0b10100, 0b10010, 0b10001),
  S: F(0b01111, 0b10000, 0b10000, 0b01110, 0b00001, 0b00001, 0b11110),
  T: F(0b11111, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100),
  U: F(0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01110),
  V: F(0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01010, 0b00100),
  W: F(0b10001, 0b10001, 0b10001, 0b10101, 0b10101, 0b10101, 0b01010),
  X: F(0b10001, 0b10001, 0b01010, 0b00100, 0b01010, 0b10001, 0b10001),
  Y: F(0b10001, 0b10001, 0b10001, 0b01010, 0b00100, 0b00100, 0b00100),
  Z: F(0b11111, 0b00001, 0b00010, 0b00100, 0b01000, 0b10000, 0b11111),
  '0': F(0b01110, 0b10001, 0b10011, 0b10101, 0b11001, 0b10001, 0b01110),
  '1': F(0b00100, 0b01100, 0b00100, 0b00100, 0b00100, 0b00100, 0b01110),
  '2': F(0b01110, 0b10001, 0b00001, 0b00010, 0b00100, 0b01000, 0b11111),
  '3': F(0b01110, 0b10001, 0b00001, 0b00110, 0b00001, 0b10001, 0b01110),
  '4': F(0b00010, 0b00110, 0b01010, 0b10010, 0b11111, 0b00010, 0b00010),
  '5': F(0b11111, 0b10000, 0b11110, 0b00001, 0b00001, 0b10001, 0b01110),
  '6': F(0b00110, 0b01000, 0b10000, 0b11110, 0b10001, 0b10001, 0b01110),
  '7': F(0b11111, 0b00001, 0b00010, 0b00100, 0b01000, 0b01000, 0b01000),
  '8': F(0b01110, 0b10001, 0b10001, 0b01110, 0b10001, 0b10001, 0b01110),
  '9': F(0b01110, 0b10001, 0b10001, 0b01111, 0b00001, 0b00010, 0b01100),
};
const GW = 5;
const GH = 7;
const LETTER_GAP = 1;
const LINE_GAP = 2;

function scatter(i: number, W: number, H: number) {
  const a = Math.sin(i * 12.9898 + 17.31) * 43758.5453;
  const b = Math.sin(i * 78.233 + 91.17) * 43758.5453;
  return {
    x: ((a - Math.floor(a)) * 2.6 - 0.8) * W,
    y: ((b - Math.floor(b)) * 2.6 - 0.8) * H,
  };
}

function easeOutCubic(t: number) {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return 1 - Math.pow(1 - t, 3);
}

/** Split text into rows so each row fits comfortably.
 *  Priority:
 *   1. Honour explicit whitespace ("Open Salaam" -> ["OPEN", "SALAAM"]).
 *   2. Detect a camelCase / PascalCase boundary in the SOURCE casing
 *      ("SooqSolar" -> ["SOOQ", "SOLAR"]).
 *   3. Fall back to mid-point split for long single-case words.
 *   4. Short words stay on one row. */
function splitToRows(text: string): string[] {
  const trimmed = text.trim();
  if (/\s/.test(trimmed)) {
    return trimmed.toUpperCase().split(/\s+/);
  }
  // CamelCase boundary: a lower-case letter immediately before an upper-case one.
  const camelMatch = trimmed.match(/^(.+?[a-z])([A-Z].+)$/);
  if (camelMatch) {
    return [camelMatch[1].toUpperCase(), camelMatch[2].toUpperCase()];
  }
  const upper = trimmed.toUpperCase();
  if (upper.length <= 5) return [upper];
  const mid = Math.ceil(upper.length / 2);
  return [upper.slice(0, mid), upper.slice(mid)];
}

export default function DotMatrixDisplay({ text, width = 480, duration = 700 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const startedAtRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const rows = splitToRows(text);
  const longestCells = Math.max(...rows.map((r) => r.length * (GW + LETTER_GAP) - LETTER_GAP));

  const pitch = Math.max(7, Math.min(16, Math.floor(width / (longestCells + 3))));
  const dotRadius = pitch * 0.46;
  const lineH = GH * pitch + LINE_GAP * pitch;
  const totalH = rows.length * GH * pitch + (rows.length - 1) * LINE_GAP * pitch;
  const padX = pitch * 1.5;
  const padY = pitch * 1;
  const stageW = longestCells * pitch + padX * 2;
  const stageH = totalH + padY * 2;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = stageW * dpr;
    canvas.height = stageH * dpr;
    canvas.style.width = `${stageW}px`;
    canvas.style.height = `${stageH}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // ---- targets ----
    const targets: { x: number; y: number; rowIdx: number }[] = [];
    const topY = (stageH - totalH) / 2;
    rows.forEach((word, rIdx) => {
      const wCells = word.length * (GW + LETTER_GAP) - LETTER_GAP;
      const wPx = wCells * pitch;
      const x0 = (stageW - wPx) / 2;
      const yBase = topY + rIdx * lineH;
      let cx = x0;
      for (const ch of word) {
        const glyph = FONT[ch] ?? FONT[' '];
        for (let r = 0; r < GH; r++) {
          const bits = glyph[r] ?? 0;
          for (let c = 0; c < GW; c++) {
            if (bits & (1 << (GW - 1 - c))) {
              targets.push({
                x: cx + c * pitch + pitch / 2,
                y: yBase + r * pitch + pitch / 2,
                rowIdx: rIdx,
              });
            }
          }
        }
        cx += (GW + LETTER_GAP) * pitch;
      }
    });

    // Minimalist: no background grid. Lit dots stand alone against pure black,
    // which reads as a focused LED segment instead of a busy panel.
    const bg: { x: number; y: number }[] = [];

    const STAGGER = (duration * 0.5) / Math.max(targets.length, 1);

    function paint(now: number) {
      if (startedAtRef.current == null) startedAtRef.current = now;
      const elapsed = now - startedAtRef.current;

      ctx!.clearRect(0, 0, stageW, stageH);

      // ── background grid (faint, flickering) ──
      ctx!.shadowBlur = 0;
      ctx!.fillStyle = '#F2F2F2';
      for (let i = 0; i < bg.length; i++) {
        const p = bg[i];
        const phase = i * 0.0231 + Math.sin(i * 0.71) * 0.5;
        const flicker = Math.sin(now * 0.0014 + phase * 8) * 0.5 + 0.5;
        ctx!.globalAlpha = 0.03 + flicker * 0.05; // 0.03 .. 0.08
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, dotRadius * 0.55, 0, Math.PI * 2);
        ctx!.fill();
      }

      // ── headline dots: scatter → converge → hold with jitter ──
      for (let i = 0; i < targets.length; i++) {
        const tgt = targets[i];
        const start = scatter(i, stageW * 1.6, stageH * 2.6);
        const localT = (elapsed - i * STAGGER) / duration;
        const k = easeOutCubic(Math.max(0, Math.min(1, localT)));

        let x = start.x + (tgt.x - start.x) * k;
        let y = start.y + (tgt.y - start.y) * k;

        const settled = k >= 1;
        if (settled) {
          const j = Math.sin(now * 0.0045 + i * 0.21) * 0.5;
          x = tgt.x + j;
          y = tgt.y + j * 0.4;
        }

        // Hot-flash signal-green during the second half of arrival (0.5..0.92).
        // Wider window than v1 so the colour actually registers.
        const isHot = k > 0.5 && k < 0.92;

        ctx!.globalAlpha = k;
        // Glow for arriving + settled dots — the "LED bulb" feel
        if (k > 0.4) {
          ctx!.shadowColor = isHot ? '#7CFFB2' : '#F2F2F2';
          ctx!.shadowBlur = isHot ? dotRadius * 2.2 : dotRadius * 1.4;
        } else {
          ctx!.shadowBlur = 0;
        }
        ctx!.fillStyle = isHot ? '#7CFFB2' : '#F2F2F2';
        ctx!.beginPath();
        ctx!.arc(x, y, dotRadius, 0, Math.PI * 2);
        ctx!.fill();
      }

      ctx!.shadowBlur = 0;
      ctx!.globalAlpha = 1;
      rafRef.current = requestAnimationFrame(paint);
    }

    rafRef.current = requestAnimationFrame(paint);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      startedAtRef.current = null;
    };
  }, [text, stageW, stageH, pitch, dotRadius, totalH, lineH, duration]);

  return (
    <canvas
      ref={canvasRef}
      aria-label={text}
      role="img"
      style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
    />
  );
}
